import { useState } from "react";
import { toast } from "sonner";
import { Download, Link2, Unplug, RefreshCw, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtMT } from "@/lib/mountainTime";
import { createPairing, revokeExtension, downloadExtensionZip, errText } from "@/lib/fbLabApi";

const CopyCode = ({ value }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const t = Object.assign(document.createElement("textarea"), { value });
      document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove();
    }
    setDone(true);
    setTimeout(() => setDone(false), 2000);
  };
  return (
    <Button type="button" size="sm" variant="outline" onClick={copy} data-testid="lab-pairing-code-copy" className="h-7 px-2 align-middle">
      {done ? <><Check className="w-3.5 h-3.5 mr-1" /><span data-testid="lab-pairing-code-copied">Copied!</span></> : <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>}
    </Button>
  );
};

const TONE = { connected: "bg-emerald-100 text-emerald-900", offline: "bg-stone-200 text-stone-700", never_connected: "bg-amber-100 text-amber-900" };
const LABEL = { connected: "Connected", offline: "Offline", never_connected: "Not connected yet" };
const FB = { logged_in: "logged in", logged_out: "logged out — log in to Facebook in Chrome", checkpoint: "security check showing — complete it yourself", unknown: "not checked yet" };

export function ExtensionCard({ status, onChange }) {
  const [code, setCode] = useState(null);
  const [downloading, setDownloading] = useState(false);
  // Fetched through the signed-in API session (same as every other Lab call), then saved as a file.
  const download = async () => {
    setDownloading(true);
    try {
      const blob = await downloadExtensionZip();
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement("a"), { href: url, download: "bahs-facebook-extension.zip" });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Extension downloaded");
    } catch (e) { toast.error(`Download failed: ${errText(e)}`); } finally { setDownloading(false); }
  };
  const x = status.extension;
  const pair = async () => { try { setCode(await createPairing()); } catch (e) { toast.error(errText(e)); } };
  const revoke = async () => {
    if (!window.confirm("Disconnect the Chrome extension? It stops picking up jobs until paired again.")) return;
    try { await revokeExtension(); toast.success("Extension disconnected"); onChange(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <section data-testid="lab-extension" className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">Chrome extension</h2>
        <span data-testid="lab-extension-state" className={`px-2 py-0.5 rounded-full text-xs font-bold ${TONE[x.state]}`}>{LABEL[x.state]}</span>
        <Button data-testid="lab-extension-refresh" size="sm" variant="ghost" onClick={onChange}><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      <p data-testid="lab-extension-detail" className="text-sm text-stone-600">
        {x.state === "connected" && <>Last contact {fmtMT(x.last_seen_at)} · version {x.version || "?"} · Facebook {FB[x.fb_session] || FB.unknown}{x.paused ? " · paused" : ""}</>}
        {x.state === "offline" && <>No contact since {fmtMT(x.last_seen_at)}. Chrome is closed, the extension is paused/removed, or this computer is offline. Nothing is shown as live.</>}
        {x.state === "never_connected" && <>Install the extension in desktop Chrome, then pair it with a code.</>}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="lab-extension-download" variant="outline" size="sm" className="rounded-full" disabled={downloading} onClick={download}><Download className="h-4 w-4 mr-1.5" />{downloading ? "Preparing…" : "Download extension"}</Button>
        <Button data-testid="lab-extension-pair" size="sm" className="rounded-full" onClick={pair}><Link2 className="h-4 w-4 mr-1.5" />Pair extension</Button>
        {x.state !== "never_connected" && <Button data-testid="lab-extension-revoke" size="sm" variant="outline" className="rounded-full text-red-700" onClick={revoke}><Unplug className="h-4 w-4 mr-1.5" />Disconnect</Button>}
      </div>
      {x.state === "connected" && x.version !== status.extension_version && <p data-testid="lab-extension-outdated" className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2">Chrome is running extension version {x.version || "?"}; the latest is {status.extension_version}. Download it again, unzip over the old folder, then click the reload icon on the extension in chrome://extensions.</p>}
      {x.current_job && <p data-testid="lab-extension-current-job" className="text-sm bg-stone-50 border rounded-lg p-2">Extension is working on a {x.current_job.kind} job · step: {x.current_job.label || x.current_job.step} · Facebook tab {x.current_job.tab_open ? "open" : "not found"}{x.current_job.tab_hidden ? " (in background)" : ""}{x.current_job.worker_restarted ? " · background worker was restarted by Chrome" : ""}</p>}
      {code && <p data-testid="lab-pairing-code" className="text-sm bg-blue-50 border border-blue-200 rounded-lg p-2">Pairing code <b className="font-mono tracking-widest text-lg">{code.code}</b> <CopyCode value={code.code} /> — enter it in the extension popup within 10 minutes (one use).</p>}
      <ol data-testid="lab-install-steps" className="text-xs text-stone-600 list-decimal pl-5 space-y-0.5">
        <li>Download and unzip the extension on a desktop computer.</li>
        <li>In Chrome open <span className="font-mono">chrome://extensions</span>, switch on Developer mode, click Load unpacked and choose the unzipped folder.</li>
        <li>Pin it, click it and enter the pairing code. Stay logged in to Facebook in that Chrome profile.</li>
      </ol>
    </section>
  );
}
